import type { Event, EventHint } from '@sentry/browser';
import * as WebSentry from '@sentry/browser';

import { Singleton } from '../../common/utils/Singleton';
import { FRUITFUL_ENV, SENTRY_DSN_WEB } from '../../config/config';
import { IS_LOCAL } from '../../config/FruitfulEnvironment';
import type { User } from '../../user/types/User';
import type { ISentry } from './types/Sentry';

const IGNORED_BROWSER_RUNTIME_ERROR_MESSAGES = [
    'Invalid call to runtime.sendMessage(). Tab not found.',
];

const WEBKIT_MESSAGE_HANDLERS_ERROR_MESSAGE =
    "undefined is not an object (evaluating 'window.webkit.messageHandlers')";
const WEBKIT_NATIVE_BRIDGE_FRAME_FUNCTIONS = [
    'sendDataToNative',
    'sendPageHideMessage',
];

const DATADOG_RESPONSE_CLONE_FRAME_PATH =
    '/node_modules/@datadog/browser-core/esm/tools/utils/responseUtils.js';

const isIgnoredBrowserRuntimeErrorMessage = (message?: string): boolean =>
    message != null &&
    IGNORED_BROWSER_RUNTIME_ERROR_MESSAGES.some((ignoredMessage) =>
        message.includes(ignoredMessage),
    );

const isNetworkErrorTypeError = (event: Event, hint: EventHint): boolean => {
    const originalException = hint.originalException;

    if (
        originalException instanceof TypeError &&
        originalException.message === 'network error'
    ) {
        return true;
    }

    return (
        event.exception?.values?.some(
            (exception) =>
                exception.type === 'TypeError' &&
                exception.value === 'network error',
        ) ?? false
    );
};

const isWebkitMessageHandlersTypeError = (
    event: Event,
    hint: EventHint,
): boolean => {
    const originalException = hint.originalException;

    if (
        originalException instanceof TypeError &&
        originalException.message === WEBKIT_MESSAGE_HANDLERS_ERROR_MESSAGE
    ) {
        return true;
    }

    return (
        event.exception?.values?.some(
            (exception) =>
                exception.type === 'TypeError' &&
                exception.value === WEBKIT_MESSAGE_HANDLERS_ERROR_MESSAGE,
        ) ?? false
    );
};

const hasDatadogResponseCloneFrame = (event: Event): boolean => {
    return (
        event.exception?.values?.some((exception) =>
            exception.stacktrace?.frames?.some(
                (frame) =>
                    frame.filename?.includes(
                        DATADOG_RESPONSE_CLONE_FRAME_PATH,
                    ) ?? false,
            ),
        ) ?? false
    );
};

const hasWebkitNativeBridgeFrame = (event: Event): boolean => {
    return (
        event.exception?.values?.some((exception) =>
            exception.stacktrace?.frames?.some(
                (frame) =>
                    frame.function != null &&
                    WEBKIT_NATIVE_BRIDGE_FRAME_FUNCTIONS.includes(
                        frame.function,
                    ),
            ),
        ) ?? false
    );
};

const isIgnoredDatadogRumResponseCloneError = (
    event: Event,
    hint: EventHint,
): boolean => {
    return (
        isNetworkErrorTypeError(event, hint) &&
        hasDatadogResponseCloneFrame(event)
    );
};

const isIgnoredWebkitNativeBridgeError = (
    event: Event,
    hint: EventHint,
): boolean => {
    return (
        isWebkitMessageHandlersTypeError(event, hint) &&
        hasWebkitNativeBridgeFrame(event)
    );
};

const isIgnoredBrowserRuntimeError = (
    event: Event,
    hint: EventHint,
): boolean => {
    const originalException = hint.originalException;

    if (
        originalException instanceof Error &&
        isIgnoredBrowserRuntimeErrorMessage(originalException.message)
    ) {
        return true;
    }

    if (
        typeof originalException === 'string' &&
        isIgnoredBrowserRuntimeErrorMessage(originalException)
    ) {
        return true;
    }

    if (isIgnoredBrowserRuntimeErrorMessage(event.message)) {
        return true;
    }

    return (
        event.exception?.values?.some((exception) =>
            isIgnoredBrowserRuntimeErrorMessage(exception.value),
        ) ?? false
    );
};

class SentryFacade extends Singleton implements ISentry {
    public readonly Sentry: typeof WebSentry;
    public readonly navigationIntegration: undefined;

    constructor() {
        super();
        this.Sentry = WebSentry;
        this.navigationIntegration = undefined;
    }

    public init(): void {
        if (!this.isEnabled()) {
            return;
        }

        this.Sentry.init({
            dsn: SENTRY_DSN_WEB,
            environment: FRUITFUL_ENV,
            integrations: [
                this.Sentry.browserTracingIntegration(),
                this.Sentry.extraErrorDataIntegration({
                    depth: 8,
                    captureErrorCause: true,
                }),
            ],
            tracesSampleRate: 0.1,
            beforeSend: (event, hint) => {
                if (
                    isIgnoredBrowserRuntimeError(event, hint) ||
                    isIgnoredWebkitNativeBridgeError(event, hint) ||
                    isIgnoredDatadogRumResponseCloneError(event, hint)
                ) {
                    return null;
                }

                return event;
            },
        });

        const scope = this.Sentry.getCurrentScope();
        scope.setTag('app', 'member-app');
        scope.setTag('platform', 'web');
    }

    public identifyUser(user: User): void {
        if (this.isEnabled()) {
            this.Sentry.setUser({
                id: user.id,
                visitorId: user.visitorId, // For connecting events and sessions before/after user creation
                membershipId: user.membership?.id, // Active membership for error/session context
                isVisitor: false, // Only true before user account creation
            });
        }
    }

    public identifyVisitor(visitorId: string): void {
        if (this.isEnabled()) {
            this.Sentry.setUser({ id: visitorId, isVisitor: true, visitorId });
        }
    }

    public clearUser(): void {
        if (this.isEnabled()) {
            this.Sentry.setUser(null);
        }
    }

    public isEnabled(): boolean {
        return !IS_LOCAL && SENTRY_DSN_WEB != null;
    }
}

export default SentryFacade.getInstance();
